if (!customElements.get('slideshow-component')) {
  customElements.define(
    'slideshow-component',
    class SlideshowComponent extends HTMLElement {
      constructor() {
        super();

        this.sliderControls = this.querySelector('.swiper-controls');
        this.sliderInstance = false;
        this.layout = this.dataset.layout;
        this.items = Number(this.dataset.items);
      }

      connectedCallback() {
        if (this.sliderInstance) return;

        this.initSlider();

        if (this.classList.contains('slideshow-height--adapt')) {
          this.calcSlideHeight();
        }
      }

      static get observedAttributes() {
        return ['selected-index'];
      }

      get selectedIndex() {
        return parseInt(this.getAttribute('selected-index')) || 0;
      }

      set selectedIndex(index) {
        this.setAttribute('selected-index', `${index}`);
      }

      initSlider() {
        const additionModules = [FoxTheme.Swiper.Autoplay, FoxTheme.Swiper.EffectFade];

        this.sliderOptions = {
          slidesPerView: 1,
          spaceBetween: 10,
          loop: true,
          grabCursor: true,
          allowTouchMove: true,
          threshold: 2,
          effect: 'fade',
          on: {
            init: this.handleAfterInit.bind(this),
          },
          fadeEffect: {
            crossFade: true,
          },
        };

        if (this.sliderControls) {
          this.sliderOptions.pagination = {
            el: this.sliderControls.querySelector('.swiper-pagination'),
            clickable: true,
            // Real, named buttons so the dots are keyboard-operable (WCAG 2.1.1 / 4.1.2).
            renderBullet: (index, className) =>
              `<button type="button" class="${className}" aria-label="Go to slide ${index + 1}"></button>`,
          };
        }

        switch (this.layout) {
          case 'centered':
            this.sliderOptions = {
              ...this.sliderOptions,
              centeredSlides: true,
              slidesPerView: 'auto',
              effect: 'slide',
            };
            break;
        }

        if (this.layout !== 'centered' && this.items === 1) {
          this.sliderOptions.loop = false;
          this.classList.remove('slideshow-controls-visible--yes');
        }

        const autoplayDelay = parseInt(this.dataset.autoplay);
        if (autoplayDelay > 0) {
          this.sliderOptions = {
            ...this.sliderOptions,
            autoplay: {
              delay: autoplayDelay,
              disableOnInteraction: false,
            },
          };
        }

        // Opt-in via section custom_class "slideshow-fx-slide": use the slide
        // effect with autoHeight instead of the default fade. Fade stacks every
        // slide absolutely at one fixed height, which breaks the "text below on
        // mobile" layout (image + text block is taller and varies per slide and
        // leaves the container/dots misaligned). Slide + autoHeight lets the
        // container hug each slide's real height, so the dots always sit on the
        // block and there is no leftover gap. Scoped, so other sliders keep fade.
        const slideshowSection = this.closest('.section--slideshow');
        if (slideshowSection && slideshowSection.classList.contains('slideshow-fx-slide')) {
          this.sliderOptions.effect = 'slide';
          this.sliderOptions.autoHeight = true;
          delete this.sliderOptions.fadeEffect;
        }

        this.sliderInstance = new window.FoxTheme.Carousel(this, this.sliderOptions, additionModules);
        this.sliderInstance.init();

        this.sliderInstance.slider.on('realIndexChange', this.handleSlideChange.bind(this));
        this.selectedIndex = this.sliderInstance.slider.realIndex;

        if (this.sliderInstance) {
          if (this.layout === 'centered' && !FoxTheme.config.mqlMobile) {
            const motionTextElements = Array.from(this.querySelectorAll('motion-element[data-text]'));
            motionTextElements &&
              motionTextElements.forEach((motionTextElement) => {
              if (
                typeof motionTextElement.resetAnimation === 'function' &&
                !motionTextElement.closest('.swiper-slide-active')
              ) {
                motionTextElement.resetAnimation();
              }
            });
          }
          this.selectedElement = this.sliderInstance.slider.slides[this.sliderInstance.slider.activeIndex];
          this.onReady(this.selectedElement, this.sliderInstance.slider.slides);
          this.initA11y();
          // Fix accessibility
          const focusableElements = FoxTheme.a11y.getFocusableElements(this);
          if (this.layout === 'centered') {
            focusableElements.forEach((element) => {
              element.addEventListener('focusin', () => {
                const slide = element.closest('.swiper-slide');
                const slideToIndex = this.sliderInstance.slider.slides.indexOf(slide);
                this.sliderInstance.slider.slideToLoop(slideToIndex);
              });
            });
          } else {
            focusableElements.forEach((element) => {
              element.addEventListener('focusin', () => {
                const slide = element.closest('.swiper-slide');
                this.sliderInstance.slider.slideTo(this.sliderInstance.slider.slides.indexOf(slide));
              });
            });
          }
        }
      }

      /**
       * Accessible carousel behaviour (WCAG 2.2.2 / 2.1.1 / 4.1.2):
       *  - pause/play, prev/next buttons (.slideshow__a11y-controls in the section markup);
       *  - autoplay pauses while keyboard focus is inside (not on hover), and resumes
       *    afterwards unless the user pressed Pause;
       *  - no live-region chatter while autoplaying;
       *  - only the visible slide is exposed: other slides (incl. loop duplicates) are
       *    inert + aria-hidden, so hidden CTAs aren't tabbable and the hero H1 is exposed once.
       */
      initA11y() {
        const swiper = this.sliderInstance && this.sliderInstance.slider;
        if (!swiper) return;
        const section = this.closest('.section--slideshow') || this;
        const controls = section.querySelector('.slideshow__a11y-controls');
        const pauseBtn = controls && controls.querySelector('[data-slideshow-pause]');
        const hasAutoplay = Boolean(swiper.params.autoplay && swiper.params.autoplay.enabled !== false && parseInt(this.dataset.autoplay) > 0);
        let userPaused = false;
        let focusInside = false;

        const setLive = (running) => this.setAttribute('aria-live', running ? 'off' : 'polite');
        const start = (force = false) => {
          if (!hasAutoplay || userPaused || !swiper.autoplay) return;
          if (!force && focusInside) return;
          swiper.autoplay.start();
          setLive(true);
        };
        const stop = () => {
          if (!hasAutoplay || !swiper.autoplay) return;
          swiper.autoplay.stop();
          setLive(false);
        };

        // Loop duplicates are visual-only: fully hidden (inert + aria-hidden), so the hero
        // H1 is exposed once. Real slides stay readable by screen readers (the page always
        // has its H1), but controls in not-visible slides leave the Tab order.
        const FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]';
        const syncSlides = () => {
          const active = swiper.slides[swiper.activeIndex];
          swiper.slides.forEach((slide) => {
            if (slide.classList.contains('swiper-slide-duplicate')) {
              slide.inert = true;
              slide.setAttribute('aria-hidden', 'true');
              return;
            }
            const visible = slide === active;
            slide.querySelectorAll(FOCUSABLE).forEach((el) => {
              if (visible) {
                if (el.hasAttribute('data-a11y-tabindex')) {
                  const orig = el.getAttribute('data-a11y-tabindex');
                  if (orig === '') el.removeAttribute('tabindex');
                  else el.setAttribute('tabindex', orig);
                  el.removeAttribute('data-a11y-tabindex');
                }
              } else if (!el.hasAttribute('data-a11y-tabindex')) {
                el.setAttribute('data-a11y-tabindex', el.getAttribute('tabindex') || '');
                el.setAttribute('tabindex', '-1');
              }
            });
          });
          const bullets = this.querySelectorAll('.swiper-pagination-bullet');
          bullets.forEach((b, i) => {
            if (i === swiper.realIndex) b.setAttribute('aria-current', 'true');
            else b.removeAttribute('aria-current');
          });
        };
        syncSlides();
        swiper.on('slideChange', syncSlides);
        swiper.on('paginationUpdate', syncSlides);

        if (controls) {
          controls.querySelector('[data-slideshow-prev]')?.addEventListener('click', () => swiper.slidePrev());
          controls.querySelector('[data-slideshow-next]')?.addEventListener('click', () => swiper.slideNext());
          pauseBtn?.addEventListener('click', () => {
            userPaused = !userPaused;
            pauseBtn.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
            pauseBtn.setAttribute('aria-label', userPaused ? 'Play slideshow' : 'Pause slideshow');
            pauseBtn.classList.toggle('is-paused', userPaused);
            if (userPaused) stop();
            else start(true); // explicit Play wins over the keyboard-focus pause
          });
        }

        if (!hasAutoplay) return;
        // No pause on hover (it made the banner feel stuck). Autoplay pauses only via the
        // Pause button (the WCAG 2.2.2 mechanism) or while KEYBOARD focus is inside the hero
        // (:focus-visible), so mouse clicks and taps never pause it.
        section.addEventListener('focusin', (e) => {
          if (!e.target.matches(':focus-visible')) return;
          focusInside = true;
          stop();
        });
        section.addEventListener('focusout', (e) => {
          if (section.contains(e.relatedTarget)) return;
          if (!focusInside) return;
          focusInside = false;
          start();
        });
      }

      onReady(selectedElement) {
        if (selectedElement.dataset.type === 'video') {
          const videoElement = FoxTheme.utils.displayedMedia(selectedElement.querySelectorAll('video-element'));
          videoElement?.play();
        }

        if (!FoxTheme.config.motionReduced) {
          const motionEls = selectedElement.querySelectorAll('motion-element');
          motionEls.forEach((motionEl) => {
            if (!motionEl || motionEl.classList.contains('is-animated')) return;

            motionEl.removeAttribute('hold');
            motionEl.preInitialize();

            setTimeout(() => {
              motionEl.initialize();
            });
          });
        }

        this.removeAttribute('data-media-loading');
      }

      handleAfterInit() {
        // Fix active bullet not transition on the first time.
        if (this.sliderControls) {
          const activeBullet = this.sliderControls.querySelector('.swiper-pagination-bullet-active');

          if (activeBullet) {
            activeBullet.classList.remove('swiper-pagination-bullet-active');
            activeBullet.offsetHeight; // Trigger reflow.
            activeBullet.classList.add('swiper-pagination-bullet-active');
          }
        }
      }

      handleSlideChange(swiper) {
        const { slides, realIndex, activeIndex } = swiper;
        this.selectedIndex = realIndex;

        this.updateControlsScheme(slides[activeIndex]);
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
          const fromElements = this.querySelectorAll(`[data-swiper-slide-index="${oldValue}"]`);
          const toElements = this.querySelectorAll(`[data-swiper-slide-index="${newValue}"]`);

          fromElements.forEach((fromElement) => {
            if (fromElement.dataset.type === 'video') {
              const videoElement = FoxTheme.utils.displayedMedia([fromElement.querySelector('video-element')]);
              videoElement && videoElement.pause();
            }

            const motionEls = fromElement.querySelectorAll('motion-element');
            motionEls.forEach((motionEl) => {
              if (motionEl.hasAttribute('data-text')) {
                motionEl.resetAnimation();
              }
            });
          });

          toElements.forEach((toElement) => {
            setTimeout(() => {
              if (toElement.classList.contains('swiper-slide-active')) {
                if (toElement.dataset.type === 'video') {
                  const videoElement = FoxTheme.utils.displayedMedia([toElement.querySelector('video-element')]);
                  videoElement && videoElement.play();
                }

                const motionEls = toElement.querySelectorAll('motion-element');
                motionEls.forEach((motionEl) => {
                  motionEl && motionEl.refreshAnimation();
                });
              }
            });
          });
        }
      }

      updateControlsScheme(activeSlide) {
        if (this.sliderControls) {
          const classesToRemove = Array.from(this.sliderControls.classList).filter((className) =>
            className.startsWith('color-')
          );
          classesToRemove.forEach((className) => this.sliderControls.classList.remove(className));
          const colorScheme = activeSlide.dataset.colorScheme;
          this.sliderControls.classList.add(colorScheme);
        }
      }

      calcSlideHeight() {
        let maxHeight = 0;
        const slides = this.querySelectorAll('.swiper-slide');
        slides &&
          slides.forEach((slide) => {
            const slideText = slide.querySelector('.slideshow__text');
            if (slideText) {
              const slideTextHeight = slideText.offsetHeight;
              if (slideTextHeight > maxHeight) {
                maxHeight = slideTextHeight;
              }
            }
          });

        if (this.classList.contains('slideshow-controls-position--bottom')) {
          maxHeight += 100; // Avoid overlap with dots.
        }

        this.style.setProperty('--slide-height', maxHeight + 'px');
      }
    }
  );
}
